import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { TravelReportModal } from './TravelReportModal.jsx';
import { TravelReportDetailModal } from './TravelReportDetailModal.jsx';

const STATUS_VARIANT = { DRAFT: 'neutral', SUBMITTED: 'warning', APPROVED: 'info', REJECTED: 'danger', REIMBURSED: 'success' };

export function TravelExpensesPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['hr', 'travel-reports'], queryFn: () => hrApi.listTravelReports().then((r) => r.items) });
  const rows = list.data || [];
  const count = (s) => rows.filter((r) => r.status === s).length;

  const columns = [
    { key: 'reportNumber', label: t('tr.col.number'), render: (r) => <span className="mono">{r.reportNumber}</span> },
    { key: 'employee', label: t('tr.col.employee'), render: (r) => r.employeeName },
    { key: 'purpose', label: t('tr.col.purpose') },
    { key: 'period', label: t('tr.col.period'), render: (r) => (r.fromDate ? `${formatDate(r.fromDate)}-${r.toDate ? formatDate(r.toDate) : ''}` : '-') },
    { key: 'total', label: t('tr.col.total'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.total)}</span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`tr.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('tr.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('tr.title')}</h1>
          <p className="muted">{t('tr.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('tr.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('tr.metric.submitted')} value={String(count('SUBMITTED'))} trendVariant={count('SUBMITTED') > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('tr.metric.approved')} value={String(count('APPROVED'))} />
        <MetricCard label={t('tr.metric.reimbursed')} value={String(count('REIMBURSED'))} trendVariant="success" />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('tr.empty')} />

      <TravelReportModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <TravelReportDetailModal reportId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
